import { Button } from "../ui/button";
import { TableCell, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { formatDateTime } from "../../lib/format";
import { MapPin, ArrowRight, FileDown, Trash2, PartyPopper, Truck, BellRing, Users, Copy, ArrowLeftRight } from "lucide-react";

const STATUS_TRIGGER_CLS = {
  pubblicata: "bg-[#F3ECDC] text-[#87713F] border-[#C6AD76]",
  assegnato: "bg-[#DCE3EC] text-[#18314F] border-[#8FA3BE]",
  completata: "bg-[#DCFCE7] text-[#15803D] border-[#BBF7D0]",
  da_fatturare: "bg-[#942F30] text-white border-[#7A2627] [&_svg]:text-white",
  annullata: "bg-[#F3DCDC] text-[#942F30] border-[#D48E8E]",
};

export const isUrgent = (r) => {
  if (r.status !== "pubblicata" || r.assigned_partner_id) return false;
  const d = new Date(String(r.operative?.data_ora_evento || "").replace(" ", "T"));
  const diff = d.getTime() - Date.now();
  return !isNaN(d.getTime()) && diff > 0 && diff < 48 * 3600000;
};

const TypeBadge = ({ r }) => r.request_type === "evento"
  ? <span data-testid={`type-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-1.5 py-0.5 text-[10px] font-medium w-fit"><PartyPopper className="h-3 w-3" /> Evento</span>
  : <span data-testid={`type-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] font-medium w-fit"><Truck className="h-3 w-3" /> Trasporto</span>;

const IconBtn = ({ testid, title, onClick, className = "text-slate-500 hover:text-primary", variant = "ghost", children }) => (
  <Button data-testid={testid} variant={variant} size="icon" className={`h-8 w-8 ${className}`} title={title} onClick={onClick}>{children}</Button>
);

export const RequestRow = ({ r, isNew, navigate, actions, canDelete = true }) => {
  const isEv = r.request_type === "evento";
  const rejectedNames = r.rejected_detail?.map((x) => x.name).join(", ");
  return (
    <TableRow data-testid={`request-row-${r.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/operativa/richiesta/${r.id}`)}>
      <TableCell className="font-mono text-xs font-semibold text-primary whitespace-nowrap">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5">
            {r.code}
            {(r.candidates || []).length > 0 && r.status === "pubblicata" && (
              <span data-testid={`candidates-badge-${r.id}`} title={`${r.candidates.length} partner candidati`}
                className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isNew ? "bg-amber-500 text-white animate-pulse" : "bg-amber-100 text-amber-800"}`}>
                <Users className="h-3 w-3" /> {r.candidates.length}
              </span>
            )}
          </span>
          <TypeBadge r={r} />
        </div>
      </TableCell>
      <TableCell className="text-sm text-slate-700 font-medium"><span className="line-clamp-2">{isEv ? (r.evento?.denominazione || "—") : (r.patient?.nominativo || "—")}</span></TableCell>
      <TableCell className="text-sm">
        {isEv ? (
          <span className="flex items-center gap-1.5 text-slate-600"><MapPin className="h-3 w-3 text-slate-400 shrink-0" /> <span className="line-clamp-2">{r.evento?.luogo || "—"}</span></span>
        ) : (
          <span className="flex items-center gap-1.5">
            <span className="truncate max-w-[110px]">{r.partenza?.citta || "-"}</span><ArrowRight className="h-3 w-3 text-slate-400 shrink-0" /><span className="truncate max-w-[110px]">{r.arrivo?.citta || "-"}</span>
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm text-slate-600 whitespace-nowrap">{formatDateTime(r.operative?.data_ora_evento) || "-"}</TableCell>
      <TableCell className="text-sm text-slate-600"><span className="line-clamp-2">{r.operative?.provenienza || "—"}</span></TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Select value={r.status} onValueChange={(v) => actions.changeStatus(r, v)}>
          <SelectTrigger data-testid={`status-select-${r.id}`} className={`h-8 w-[130px] font-semibold ${STATUS_TRIGGER_CLS[r.status] || ""}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pubblicata">Pubblicata</SelectItem><SelectItem value="assegnato">Assegnato</SelectItem><SelectItem value="completata">Completata</SelectItem>
            <SelectItem value="da_fatturare">Fatturare</SelectItem><SelectItem value="annullata">Annullata</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-sm text-slate-600">
        <span className="line-clamp-2">{r.assigned_partner_name || "—"}</span>
        {rejectedNames && <span data-testid={`rejected-by-${r.id}`} className="block mt-0.5 text-[11px] text-[#942F30] leading-tight" title={rejectedNames}>Rifiutato da: {rejectedNames}</span>}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-center gap-0.5">
          <Switch data-testid={`fatturato-toggle-${r.id}`} checked={!!r.fatturato} onCheckedChange={() => actions.toggleFatturato(r)} />
          <span className={`text-[10px] font-medium ${r.fatturato ? "text-emerald-600" : "text-amber-600"}`}>{r.fatturato ? "Sì" : "No"}</span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-0.5">
          {isUrgent(r) && (
            <IconBtn testid={`sollecita-${r.id}`} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={(e) => actions.sollecita(e, r)}
              title={r.last_sollecito_at ? `Sollecita partner (ultimo: ${formatDateTime(r.last_sollecito_at)})` : "Sollecita: reinvia email a tutti i partner"}><BellRing className="h-4 w-4" /></IconBtn>
          )}
          {r.status !== "annullata" && <IconBtn testid={`download-pdf-${r.id}`} title="Scarica modulo PDF" onClick={(e) => actions.downloadPdf(e, r)}><FileDown className="h-4 w-4" /></IconBtn>}
          <IconBtn testid={`duplicate-request-${r.id}`} title="Duplica scheda" onClick={(e) => { e.stopPropagation(); navigate(isEv ? `/operativa/nuovo-evento?duplica=${r.id}` : `/operativa/nuova?duplica=${r.id}`); }}><Copy className="h-4 w-4" /></IconBtn>
          {!isEv && <IconBtn testid={`duplicate-return-${r.id}`} title="Duplica come ritorno (inverte partenza/arrivo)" onClick={(e) => { e.stopPropagation(); navigate(`/operativa/nuova?duplica=${r.id}&ritorno=1`); }}><ArrowLeftRight className="h-4 w-4" /></IconBtn>}
          {canDelete && <IconBtn testid={`delete-request-${r.id}`} title="Elimina richiesta" className="text-slate-400 hover:text-red-600" onClick={(e) => actions.deleteRequest(e, r)}><Trash2 className="h-4 w-4" /></IconBtn>}
          <ArrowRight className="h-4 w-4 text-slate-400" />
        </div>
      </TableCell>
    </TableRow>
  );
};
